import React from 'react';
import { Link } from 'react-router-dom';
import StoryArtwork from '../../StoryArtwork/StoryArtwork';
import './ArtworkTile.css';

export type ArtworkAspectRatio = '1/1' | '4/3';

export interface ArtworkSource {
  src?: string | null;
  srcSet?: string | null;
  sizes?: string;
}

interface ArtworkTileBase {
  title: string;
  artwork?: ArtworkSource | string | null;
  aspectRatio: ArtworkAspectRatio;
  /** Up to two supplemental lines under the title. */
  metadata?: string[];
  duration?: string;
  status?: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  'data-testid'?: string;
}

export interface ArtworkTileLinkProps extends ArtworkTileBase {
  to: string;
  onActivate?: never;
  disabled?: never;
}

export interface ArtworkTileActionProps extends ArtworkTileBase {
  onActivate: () => void;
  to?: never;
  disabled?: boolean;
  /** Selectable tile: exposes aria-pressed on the button form. */
  pressed?: boolean;
}

export type ArtworkTileProps = ArtworkTileLinkProps | ArtworkTileActionProps;

/**
 * One activation target per story: an h3 wrapping a single full-area link or
 * button that contains artwork, an optional badge, the title and up to two
 * metadata lines. Nothing inside it is separately interactive.
 */
const ArtworkTile: React.FC<ArtworkTileProps> = (props) => {
  const { title, artwork, aspectRatio, metadata = [], duration, status, className, loading, 'data-testid': testId } = props;
  const source = typeof artwork === 'string' ? { src: artwork } : artwork || {};
  const badge = duration || status;
  const lines = metadata.slice(0, 2);

  const body = (
    <>
      <span className="luma-tile__frame">
        <StoryArtwork
          src={source.src}
          srcSet={source.srcSet}
          sizes={source.sizes}
          alt=""
          imageClassName="luma-tile__image"
          fallbackClassName="luma-tile__fallback"
          fallbackSize={32}
          loading={loading}
        />
        {badge && <span className="luma-tile__badge">{badge}</span>}
      </span>
      <span className="luma-tile__title">{title}</span>
      <span className="luma-tile__meta">
        {lines.map((line, index) => (
          <span key={`${index}-${line}`} className="luma-tile__meta-line">{line}</span>
        ))}
      </span>
    </>
  );

  const classes = `luma-tile${className ? ` ${className}` : ''}`;

  return (
    <article className={classes} data-aspect={aspectRatio} data-pressed={props.to === undefined && props.pressed ? 'true' : undefined} data-testid={testId}>
      <h3 className="luma-tile__heading">
        {props.to !== undefined ? (
          <Link className="luma-tile__target" to={props.to}>
            {body}
          </Link>
        ) : (
          <button type="button" className="luma-tile__target" onClick={props.onActivate} disabled={props.disabled} aria-pressed={props.pressed}>
            {body}
          </button>
        )}
      </h3>
    </article>
  );
};

export default ArtworkTile;
